'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import React from 'react'
import { ChevronRight } from 'lucide-react'
import { motion } from 'motion/react'

import { filterFieldTextClassName } from '@/components/CategoryFilterNav/filterFieldText'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getApartmentHref } from '@/lib/apartments/apartmentPaths'
import type { Locale } from '@/lib/i18n/config'
import { getLocationHref } from '@/lib/mock/locationPages'
import type { ApartmentAvailabilityItem } from '@/lib/apartments/apartmentsQuery'
import { cn } from '@/utilities/ui'

export type RoomBreadcrumbsCopy = {
  locations: string
  roomTypes: string
}

type RoomBreadcrumbsProps = {
  apartment: ApartmentAvailabilityItem
  copy: RoomBreadcrumbsCopy
  locale: Locale
}

export function RoomBreadcrumbs({ apartment, copy, locale }: RoomBreadcrumbsProps) {
  const cityHref = `/cities/${apartment.locationCitySlug}`
  const locationHref = getLocationHref(apartment.locationCitySlug, apartment.locationSlug, locale)

  return (
    <nav aria-label="Breadcrumb" className="font-outfit text-sm text-white/80">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link className="transition-colors hover:text-white" href={copy.roomTypes}>
            {copy.locations}
          </Link>
        </li>
        <li aria-hidden>
          <ChevronRight className="size-4" />
        </li>
        <li>
          <Link className="transition-colors hover:text-white" href={cityHref}>
            {apartment.location.split(',')[0]?.trim()}
          </Link>
        </li>
        <li aria-hidden>
          <ChevronRight className="size-4" />
        </li>
        <li>
          <Link className="transition-colors hover:text-white" href={locationHref}>
            {apartment.locationTitle}
          </Link>
        </li>
        <li aria-hidden>
          <ChevronRight className="size-4" />
        </li>
        <li>
          <span aria-current="page" className="text-white">
            {apartment.name}
          </span>
        </li>
      </ol>
    </nav>
  )
}

type RoomVariantTabsProps = {
  activeSlug: string
  copy: {
    ariaLabel: string
  }
  locale: Locale
  siblings: ApartmentAvailabilityItem[]
}

const ROOM_VARIANT_TAB_INDICATOR_ID = 'room-variant-tab-indicator'
const ROOM_VARIANT_TAB_RADIUS_CLASS_NAME = 'rounded-[10px]'
const ROOM_VARIANT_TAB_INNER_RADIUS_CLASS_NAME = 'rounded-[8px]'

const mobileTriggerClassName = cn(
  'flex !h-11 w-full items-center justify-between gap-3 border border-black/10 !bg-white/70 px-6 py-0 !shadow-none !ring-0',
  ROOM_VARIANT_TAB_RADIUS_CLASS_NAME,
  'hover:!bg-white/70 dark:!bg-white/70 dark:hover:!bg-white/70',
  'focus-visible:!border-black/10 focus-visible:outline-none focus-visible:!ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
  filterFieldTextClassName,
  '[&>svg:last-child]:size-5 [&>svg:last-child]:shrink-0 [&>svg:last-child]:text-dark-800/90 [&>svg:last-child]:transition-transform',
  'data-[state=open]:[&>svg:last-child]:rotate-180',
  '[&_[data-slot=select-value]]:truncate [&_[data-slot=select-value]]:text-left',
)

const mobileContentClassName =
  'z-30 w-[var(--radix-select-trigger-width)] min-w-0 overflow-hidden rounded-xl border border-black/10 bg-white py-1 outline-none [box-shadow:none] !shadow-none !ring-0 !ring-offset-0 data-open:animate-none data-closed:animate-none'

const mobileItemClassName = cn(
  'rounded-none px-6 py-3 font-outfit text-sm font-semibold text-black/80',
  'focus:!bg-transparent focus:!text-black/80 focus:**:!text-black/80',
  'data-highlighted:!bg-transparent data-highlighted:!text-black/80 data-highlighted:**:!text-black/80',
  'data-[state=checked]:!bg-[#B4F0DD]/60 data-[state=checked]:!text-black data-[state=checked]:**:!text-black',
  'data-highlighted:data-[state=checked]:!bg-[#B4F0DD]/60 data-highlighted:data-[state=checked]:!text-black',
  '[&>span:first-child]:hidden',
)

function formatVariantTabLabel(apartment: ApartmentAvailabilityItem): string {
  return `${apartment.name} / ${apartment.size}m²`
}

function getVariantTabLines(apartment: ApartmentAvailabilityItem): {
  sizeLabel: string
  title: string
} {
  const sizeSuffixPattern = new RegExp(`\\s*[-/]?\\s*${apartment.size}\\s*m²\\s*$`, 'i')
  const title = sizeSuffixPattern.test(apartment.name)
    ? apartment.name.replace(sizeSuffixPattern, '').trim()
    : apartment.name

  return {
    title,
    sizeLabel: `${apartment.size}m²`,
  }
}

export function RoomVariantTabs({ activeSlug, copy, locale, siblings }: RoomVariantTabsProps) {
  const router = useRouter()

  if (siblings.length <= 1) {
    return null
  }

  const hrefBySlug = Object.fromEntries(
    siblings.map((sibling) => [
      sibling.slug,
      getApartmentHref(sibling.locationCitySlug, sibling.locationSlug, sibling.slug, locale),
    ]),
  ) as Record<string, string>

  return (
    <nav aria-label={copy.ariaLabel} className="w-full">
      <div className="w-full lg:hidden">
        <Select
          onValueChange={(slug) => {
            const href = hrefBySlug[slug]
            if (href) router.push(href)
          }}
          value={activeSlug}
        >
          <SelectTrigger
            aria-label={copy.ariaLabel}
            className={mobileTriggerClassName}
            size="default"
            style={{ boxShadow: 'none', outline: 'none' }}
          >
            <SelectValue />
          </SelectTrigger>

          <SelectContent
            align="start"
            aria-label={copy.ariaLabel}
            avoidCollisions
            className={mobileContentClassName}
            collisionPadding={16}
            hideScrollButtons
            position="popper"
            sideOffset={4}
            style={{ boxShadow: 'none', outline: 'none' }}
            viewportClassName="max-h-60 overflow-y-auto overscroll-contain data-[position=popper]:!h-auto data-[position=popper]:max-h-60"
          >
            {siblings.map((sibling) => (
              <SelectItem
                key={sibling.id}
                className={mobileItemClassName}
                textValue={formatVariantTabLabel(sibling)}
                value={sibling.slug}
              >
                {formatVariantTabLabel(sibling)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ul
        className={cn(
          'hidden w-full items-stretch gap-0.5 border border-black/10 bg-white p-1 lg:flex',
          ROOM_VARIANT_TAB_RADIUS_CLASS_NAME,
        )}
      >
        {siblings.map((sibling) => {
          const isActive = sibling.slug === activeSlug
          const href = hrefBySlug[sibling.slug]
          const { sizeLabel, title } = getVariantTabLines(sibling)

          return (
            <li key={sibling.id} className="flex min-w-0 flex-1">
              <Link
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'relative flex min-h-14 w-full flex-col items-center justify-center gap-0.5 px-3 py-2.5 text-center font-outfit text-dark-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
                  ROOM_VARIANT_TAB_INNER_RADIUS_CLASS_NAME,
                  !isActive && 'hover:text-dark-800/70',
                )}
                href={href}
              >
                {isActive ? (
                  <motion.span
                    aria-hidden
                    className={cn(
                      'absolute inset-0 bg-[#B4F0DD]',
                      ROOM_VARIANT_TAB_INNER_RADIUS_CLASS_NAME,
                    )}
                    layoutId={ROOM_VARIANT_TAB_INDICATOR_ID}
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                  />
                ) : null}
                <span className="relative z-10 max-w-full truncate text-base font-semibold leading-tight">
                  {title}
                </span>
                <span className="relative z-10 text-sm font-normal leading-tight text-dark-800/70">
                  {sizeLabel}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
